import { h } from 'preact';
import { useEffect } from 'preact/hooks';
import { trackPDPKeyIngredientClick } from 'utils/tracking';
import { Close } from '../utils/icon/icons';
import TrapFocus from '../utils/trap-focus';

export const KeyDetails = ({ detailsFlyout }) => {
  const { openFlyout, flyoutTitle, productIcons, isBundlePlp } = detailsFlyout;

  return (
    <div className={`${!isBundlePlp && 'py-5 mb-5 border-t border-t-grey-4 border-b border-b-grey-4'}`}>
      <div className="flex items-center justify-between text-body-2 font-medium">
        <p>{flyoutTitle}</p>
        {!isBundlePlp && (
          <button className="underline text-body-2" type="button" onClick={openFlyout}>
            {window.TMW.Strings.product.fullList}
          </button>
        )}
      </div>

      {!!productIcons?.length && (
        <div className="w-full pt-5 grid grid-cols-1 gap-2.5 md:gap-8 md:grid-cols-3">
          {productIcons.map((icon, index) => {
            const { title, shortDescription, image } = icon;
            const id = title.toLowerCase().replace(' ', '-').concat(`-${index}`);

            return (
              <div
                className="flex items-center gap-3 md:gap-0 md:flex-col md:justify-start"
                key={id}
                onClick={() => {
                  trackPDPKeyIngredientClick(title);
                }}
              >
                {image && (
                  <div
                    className="min-w-[56px] w-14 h-14 [&>img]:w-14 [&>img]:h-14 [&>img]:object-cover"
                    dangerouslySetInnerHTML={{ __html: image }}
                  />
                )}
                <div className="flex flex-col justify-center md:items-center">
                  {title && <p className="text-headline-tag md:text-center md:pt-3">{title}</p>}
                  {shortDescription && <p className="md:text-center text-body-light-2">{shortDescription}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FlyoutPanel = ({ detailsFlyout }) => {
  const {
    flyoutState,
    closeFlyout,
    flyoutOpenElement,
    flyoutTitle,
    productIcons,
    flyoutContentMiddle,
    commitmentTitle,
    commitmentIcons,
    flyoutContentEnd,
  } = detailsFlyout;

  let trapFocus = null;

  useEffect(() => {
    if (flyoutState) {
      if (!trapFocus) trapFocus = new TrapFocus(document.querySelector('[data-flyout-panel]'));
      document.body.classList.add('no-scroll');
      trapFocus.init();
    }

    return () => {
      document.body.classList.remove('no-scroll');
      if (trapFocus) trapFocus.destroy(flyoutOpenElement);
    };
  }, [flyoutState, trapFocus, flyoutOpenElement]);

  return (
    <div className={`flyout-panel ${flyoutState ? 'flyout-panel--open' : ''}`}>
      <div className="flyout-panel__overlay" onClick={closeFlyout}></div>
      <div className="flyout-panel__key-panel" data-flyout-panel>
        <button
          className="flyout-panel__close"
          type="button"
          aria-label={window.TMW.Strings.icons.close}
          title={window.TMW.Strings.icons.close}
          tabIndex="0"
          onClick={closeFlyout}
        >
          <Close />
        </button>

        {flyoutTitle && <h2 className="mr-5 md:mr-0">{flyoutTitle}</h2>}

        <div className="h-[calc(100%-24px)] overflow-y-auto pr-5 md:pr-0 md:h-auto md:overflow-hidden">
          {!!productIcons?.length && (
            <div className="w-full grid grid-cols-1 gap-[30px] pt-[30px] pb-10">
              {productIcons.map((icon, index) => {
                const { title, longDescription, image, url } = icon;
                const id = title.toLowerCase().replace(' ', '-').concat(`-${index}`);

                return (
                  <div className="flex gap-4" key={id}>
                    {image && (
                      <div
                        className="w-20 [&>img]:w-20 [&>img]:h-20 [&>img]:object-cover"
                        dangerouslySetInnerHTML={{ __html: image }}
                      />
                    )}
                    <div className="flex-1 flex flex-col gap-2">
                      {title && (
                        <div className="relative max-w-max">
                          <p className="!text-body-2 !font-medium !mb-0">{title}</p>
                          {url && (
                            <a
                              className="absolute inset-0"
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={title}
                            ></a>
                          )}
                        </div>
                      )}
                      {longDescription && <p className="!text-body-light !mb-0">{longDescription}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rte" dangerouslySetInnerHTML={{ __html: flyoutContentMiddle }} />

          {commitmentTitle && <h2 className="pt-10">{commitmentTitle}</h2>}

          {!!commitmentIcons?.length && (
            <div className="flex flex-wrap gap-10 md:gap-[50px]">
              {commitmentIcons.map((item, index) => {
                const { title, icon } = item;
                const id = title.toLowerCase().replace(' ', '-').concat(`-${index}`);

                return (
                  <div className="flex flex-col items-center gap-2.5" key={id}>
                    {icon && (
                      <div
                        className="min-w-[100px] max-w-[100px] min-h-[100px] max-h-[100px] [&>img]:w-[100px] [&>img]:h-[100px] [&>img]:object-cover"
                        dangerouslySetInnerHTML={{ __html: icon }}
                      />
                    )}
                    {title && <p className="text-center text-body-3 font-medium">{title}</p>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="pb-10 mt-10 md:pb-0 rte" dangerouslySetInnerHTML={{ __html: flyoutContentEnd }} />
        </div>
      </div>
    </div>
  );
};

export default FlyoutPanel;
